// @mui
import { styled } from '@mui/material/styles';
import {
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  Box,
  Avatar,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import { PATH_DASHBOARD } from 'src/routes/paths';
import cssStyles from 'src/utils/cssStyles';
// @types
import { useNavigate } from 'react-router';
import Label from 'src/components/Label';
import { baseUrl } from 'src/api/baseUrl';
import { DateConverter } from 'src/sections/common/DateConverter';
import { ICustomerDetails } from 'src/@types/Choqorak/MainTables/Contract/Customer';
import useLocales from 'src/hooks/useLocales';
import { IContractInfo } from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import { IElectricityPayment } from 'src/@types/Choqorak/MainTables/ElectricityPayment/ElectricityPayment';
import { IRentPayment } from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';
import HistoryComponent from '../../History/HistoryComponent';
import CustomerPDF from './CustomerPDF';
import CustomerPrintToolbar from './CustomerPrintToolbar';

type Props = {
  row: ICustomerDetails;
  electricityRow?: IElectricityPayment[];
  rentRow?: IRentPayment[];
  contractRow?: IContractInfo[];
  clearCustomer: () => void;
};
const OverlayStyle = styled('div')(({ theme }) => ({
  ...cssStyles().bgBlur({ blur: 0.2, color: theme.palette.primary.darker }),
  top: 0,
  zIndex: 8,
  content: "''",
  width: '100%',
  height: '100%',
  position: 'absolute',
}));

export default function CustomerDetailTableRow({
  row,
  clearCustomer,
  electricityRow,
  rentRow,
  contractRow,
}: Props) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const language = window.localStorage.getItem('i18nextLng');
  const {
    bloodGroup,
    tazkiraNo,
    dateOfBirth,
    branch,
    emergencyPhoneNumber,
    englishFirstName,
    englishSurName,
    gender,

    pashtoFirstName,
    pashtoSurName,
    permenantAddress,
    personalEmail,
    phoneNumber,
    photoPath,
    temporaryAddress,
    mainDistrict,
    mainProvince,
    currentProvince,
    currentDistrict,
    englishFatherName,
    pashtoFatherName,
    pashtoGrandFatherName,
    englishGrandFatherName,
  } = row;

  let Url = baseUrl;

  return (
    <>
      <Grid item xs={6} md={6}>
        <Stack direction="row" spacing={1.5} sx={{ mt: 1, ml: 0, mb: 2 }}>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
            onClick={() => {
              navigate(PATH_DASHBOARD.Customer.list);
              clearCustomer();
            }}
          >
            {translate('CRUD.BackToList')}
          </Button>
          <Grid
            item
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Use justifyContent instead of justifyItems
          >
            <CustomerPrintToolbar CustomerDetails={row} />
          </Grid>
        </Stack>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ py: 2, px: 2 }}>
            <Card sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  alt={englishFirstName}
                  src={Url + photoPath}
                  sx={{
                    width: 90,
                    height: 90,
                    zIndex: 11,
                    left: 0,
                    right: 0,
                    bottom: -32,
                    mx: 'auto',
                    position: 'absolute',
                  }}
                />
                <OverlayStyle />
                <Image src="/Images/ChorqorakBuilding.jpg" alt="" ratio="16/9" />
              </Box>

              <Typography variant="subtitle1" sx={{ mt: 6 }}>
                {language === 'en'
                  ? englishFirstName + ' ' + englishSurName
                  : pashtoFirstName + ' ' + pashtoSurName}
              </Typography>

              <Stack alignItems="center">
                <Typography variant="body2" sx={{ color: 'text.secondary', my: 2.5 }}>
                  {bloodGroup}
                </Typography>
              </Stack>
            </Card>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ minWidth: 275, borderRadius: '15px' }}>
            <Typography
              variant="h5"
              sx={{ fontSize: 14, textAlign: 'left', mb: 2, mt: 2, ml: 3, color: 'warning.main' }}
              color="text.secondary"
              gutterBottom
            >
              <Label
                variant="ghost"
                sx={{ textTransform: 'capitalize', fontSize: 17, p: 2 }}
                color="warning"
              >
                {translate('Customer.CustomerGeneralInfos')}
              </Label>
            </Typography>

            <TableContainer component={Paper} sx={{ p: 2 }}>
              <Table size="medium">
                <TableBody>
                  {[
                    {
                      label: translate('Customer.FatherName'),
                      value: language === 'en' ? englishFatherName : pashtoFatherName,
                    },
                    {
                      label: translate('Customer.GrandFatherName'),
                      value: language === 'en' ? englishGrandFatherName : pashtoGrandFatherName,
                    },
                    {
                      label: translate('Customer.DateOfBirth'),
                      value: <DateConverter date={dateOfBirth} />,
                    },
                    { label: translate('Customer.PersonalEmail'), value: personalEmail },
                    { label: translate('Customer.PhoneNumber'), value: phoneNumber },
                    { label: translate('Customer.CNIC'), value: tazkiraNo },
                  ].map((item, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{ fontWeight: 'bold', color: 'text.disabled', fontSize: '14px' }}
                      >
                        {item.label}:
                      </TableCell>
                      <TableCell sx={{ fontSize: '14px' }}>{item.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* <Divider sx={{ borderStyle: 'dashed' }} /> */}
        {/* sx={{ mt: 2, mr: 2, ml: 2, mb: 2 }} */}

        <Grid item xs={12} md={12}>
          <Card sx={{ p: 2 }}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: '100%' }} size="small" aria-label="a dense table">
                {/* <caption>A basic table example with a caption</caption> */}
                <TableBody>
                  <TableRow sx={{ borderBottom: '1px solid rgba(60,60,60,.9)' }}>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.MainProvince')}
                    </TableCell>
                    <TableCell align="left">{mainProvince}</TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.CurrentProvince')}
                    </TableCell>
                    <TableCell align="left">{currentProvince}</TableCell>
                  </TableRow>
                  <TableRow sx={{ borderBottom: '1px solid rgba(60,60,60,.9)' }}>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.MainDistrict')}
                    </TableCell>
                    <TableCell align="left">{mainDistrict}</TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.CurrentDistrict')}
                    </TableCell>
                    <TableCell align="left">{currentDistrict}</TableCell>
                  </TableRow>
                  <TableRow sx={{ borderBottom: '1px solid rgba(60,60,60,.9)' }}>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.TemporaryAddress')}
                    </TableCell>
                    <TableCell align="left">{temporaryAddress}</TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.PermanentAddress')}
                    </TableCell>
                    <TableCell align="left">{permenantAddress}</TableCell>
                  </TableRow>

                  <TableRow sx={{ borderBottom: '1px solid rgba(60,60,60,.9)' }}>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.Gender')}
                    </TableCell>
                    <TableCell align="left">{gender}</TableCell>
                  </TableRow>

                  <TableRow sx={{ borderBottom: '1px solid rgba(60,60,60,.9)' }}>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '16px' }}
                    >
                      {translate('Customer.EmergencyPhoneNumber')}
                    </TableCell>
                    <TableCell align="left" dir={language === 'en' ? 'ltr' : 'ltr'}>
                      {emergencyPhoneNumber}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
        <HistoryComponent
          electricityRow={electricityRow}
          rentRow={rentRow}
          contractRow={contractRow}
          historyTitle={translate('Customer.Customer')}
        />
      </Grid>
    </>
  );
}
